import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Sentry } from '@/lib/monitoring/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorId?: string;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true, errorId: crypto.randomUUID().slice(0, 8) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route error', error, info);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center px-6 text-center">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Something went wrong</h2>
          <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
            We could not load this page. Try again or contact support if the problem continues.
          </p>
          {this.state.errorId && (
            <p className="mt-2 text-xs text-slate-400">Reference: {this.state.errorId}</p>
          )}
          <div className="mt-6 flex gap-2">
            <Button type="button" onClick={() => window.location.reload()}>
              Retry
            </Button>
            <Button type="button" variant="secondary" onClick={() => (window.location.href = '/org')}>
              Go to dashboard
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
